import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Terminal, Search, Play, AlertTriangle, ChevronDown, ChevronRight,
  Clock, CheckCircle, XCircle, Zap, Loader2, Activity, Mail, Bell, ShieldCheck, Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const GROUP_META = {
  diagnostics: { label: 'Diagnostics', color: 'emerald', description: 'Read-only checks and audits' },
  maintenance: { label: 'Maintenance', color: 'amber', description: 'Routine cleanup and repair' },
  data_ops: { label: 'Data Ops', color: 'rose', description: 'Sponsor/study/participant lifecycle' },
  setup: { label: 'Setup & Seeding', color: 'indigo', description: 'Create roles, permissions, and test data' },
};

const QUICK_ACTION_COMMANDS = [
  { command: 'audit_system', icon: ShieldCheck },
  { command: 'check_reminder_system', icon: Bell },
  { command: 'check_mail_queue', icon: Mail },
  { command: 'check_password_expiration', icon: Key },
  { command: 'analyze_permissions', icon: Activity },
];

const groupPillClass = (color, active) => {
  const map = {
    emerald: active ? 'bg-emerald-600 text-white' : 'text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
    amber:   active ? 'bg-amber-600 text-white'   : 'text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30',
    rose:    active ? 'bg-rose-600 text-white'    : 'text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/30',
    indigo:  active ? 'bg-indigo-600 text-white'  : 'text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30',
    gray:    active ? 'bg-gray-700 text-white'    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
  };
  return map[color] || map.gray;
};

const CommandRunner = () => {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [formState, setFormState] = useState({});
  const [runningId, setRunningId] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/api/dev/management-commands/');
        setCommands(res.data?.commands || []);
      } catch (e) {
        console.error('Failed to load management commands:', e);
        setError(e.response?.data?.error || e.message || 'Failed to load commands');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const commandsByName = useMemo(() => {
    const map = {};
    commands.forEach((c) => { map[c.command] = c; });
    return map;
  }, [commands]);

  const groupCounts = useMemo(() => {
    const counts = { all: commands.length };
    commands.forEach((c) => { counts[c.group] = (counts[c.group] || 0) + 1; });
    return counts;
  }, [commands]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commands.filter((c) => {
      if (activeGroup !== 'all' && c.group !== activeGroup) return false;
      if (!q) return true;
      return (
        c.command.toLowerCase().includes(q) ||
        c.label.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q)
      );
    });
  }, [commands, search, activeGroup]);

  const toggleExpand = (cmd) => {
    if (expandedId === cmd.command) {
      setExpandedId(null);
      return;
    }
    const defaults = {};
    cmd.positional.forEach((p) => { defaults[p.name] = ''; });
    Object.entries(cmd.args).forEach(([name, spec]) => {
      defaults[name] = spec.type === 'bool' ? false : '';
    });
    defaults.__confirm = '';
    setFormState(defaults);
    setExpandedId(cmd.command);
  };

  const updateField = (name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const runCommand = async (cmd, overrideArgs = null) => {
    const args = {};
    if (overrideArgs) {
      Object.assign(args, overrideArgs);
    } else {
      cmd.positional.forEach((p) => {
        const v = formState[p.name];
        if (v !== '' && v != null) args[p.name] = v;
      });
      Object.entries(cmd.args).forEach(([name, spec]) => {
        const v = formState[name];
        if (spec.type === 'bool') {
          if (v) args[name] = true;
        } else if (v !== '' && v != null) {
          args[name] = v;
        }
      });
    }

    // Validate required positionals
    const missingPositional = cmd.positional.find((p) => p.default === null && !(p.name in args));
    if (missingPositional) {
      toast.error(`"${missingPositional.name}" is required`);
      return;
    }
    // Validate required named args
    const missingNamed = Object.entries(cmd.args).find(([n, s]) => s.required && !(n in args));
    if (missingNamed) {
      toast.error(`"${missingNamed[0]}" is required`);
      return;
    }

    if (cmd.destructive && !overrideArgs && formState.__confirm !== 'RUN') {
      toast.error('Type RUN to confirm destructive command');
      return;
    }

    setRunningId(cmd.command);
    setLastResult(null);
    try {
      const payload = { command: cmd.command, args };
      if (cmd.destructive) payload.confirm = 'RUN';
      const res = await api.post('/api/dev/run-command/', payload);
      setLastResult({ ...res.data, ok: true });
      toast.success(`${cmd.label} completed in ${res.data.duration_ms}ms`);
    } catch (e) {
      const data = e.response?.data || {};
      setLastResult({
        ...data,
        ok: false,
        command: cmd.command,
        error: data.error || e.message,
      });
      toast.error(`${cmd.label} failed`);
    } finally {
      setRunningId(null);
    }
  };

  const renderArgInput = (cmd, name, spec) => {
    const value = formState[name];
    const common = 'w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
    if (spec.type === 'bool') {
      return (
        <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => updateField(name, e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          <span>{spec.help || name}</span>
        </label>
      );
    }
    return (
      <input
        type={spec.type === 'int' ? 'number' : 'text'}
        value={value || ''}
        onChange={(e) => updateField(name, e.target.value)}
        placeholder={spec.help || name}
        className={common}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading commands...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-center">
        <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
        {error}
      </div>
    );
  }

  const quickActions = QUICK_ACTION_COMMANDS
    .map(({ command, icon }) => ({ icon, cmd: commandsByName[command] }))
    .filter((q) => q.cmd);

  return (
    <div className="space-y-6">
      {/* Quick actions */}
      {quickActions.length > 0 && (
        <div>
          <div className="flex items-center mb-3">
            <Zap className="w-4 h-4 mr-2 text-amber-500" />
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Quick Actions</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {quickActions.map(({ cmd, icon: Icon }) => {
              const isRunning = runningId === cmd.command;
              return (
                <button
                  key={cmd.command}
                  disabled={isRunning || runningId !== null}
                  onClick={() => runCommand(cmd, {})}
                  className="flex flex-col items-start p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-400 hover:shadow-md transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between w-full mb-2">
                    <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    {isRunning ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    ) : (
                      <Play className="w-3 h-3 text-gray-400" />
                    )}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{cmd.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{cmd.description}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search commands..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveGroup('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${groupPillClass('gray', activeGroup === 'all')}`}
          >
            All ({groupCounts.all || 0})
          </button>
          {Object.entries(GROUP_META).map(([id, meta]) => (
            <button
              key={id}
              onClick={() => setActiveGroup(id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${groupPillClass(meta.color, activeGroup === id)}`}
            >
              {meta.label} ({groupCounts[id] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Command list */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            No commands match your filters.
          </div>
        )}
        {filtered.map((cmd) => {
          const meta = GROUP_META[cmd.group] || GROUP_META.diagnostics;
          const isExpanded = expandedId === cmd.command;
          const isRunning = runningId === cmd.command;
          const hasArgs = cmd.positional.length > 0 || Object.keys(cmd.args).length > 0;
          return (
            <div
              key={cmd.command}
              className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleExpand(cmd)}
                className="w-full flex items-center p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{cmd.label}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${groupPillClass(meta.color, false)}`}>
                      {meta.label}
                    </span>
                    {cmd.destructive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        destructive
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{cmd.description}</div>
                  <code className="text-xs text-gray-400 dark:text-gray-500 font-mono">{cmd.command}</code>
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50">
                      {hasArgs ? (
                        <>
                          {cmd.positional.length > 0 && (
                            <div>
                              <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Positional</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {cmd.positional.map((p) => (
                                  <div key={p.name}>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      {p.name}
                                      {p.default === null && <span className="text-red-500 ml-1">*</span>}
                                      {p.default && p.default !== '' && (
                                        <span className="text-gray-400 ml-1">(default: {String(p.default)})</span>
                                      )}
                                    </label>
                                    <input
                                      type="text"
                                      value={formState[p.name] || ''}
                                      onChange={(e) => updateField(p.name, e.target.value)}
                                      placeholder={p.default != null ? String(p.default) : p.name}
                                      className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {Object.keys(cmd.args).length > 0 && (
                            <div>
                              <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Options</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(cmd.args).map(([name, spec]) => (
                                  <div key={name}>
                                    {spec.type !== 'bool' && (
                                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        --{name}
                                        {spec.required && <span className="text-red-500 ml-1">*</span>}
                                      </label>
                                    )}
                                    {renderArgInput(cmd, name, spec)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No arguments.</p>
                      )}

                      {cmd.destructive && (
                        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                          <label className="block text-xs font-medium text-red-700 dark:text-red-300 mb-1 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Type <code className="mx-1 px-1 rounded bg-red-100 dark:bg-red-900/40">RUN</code> to confirm
                          </label>
                          <input
                            type="text"
                            value={formState.__confirm || ''}
                            onChange={(e) => updateField('__confirm', e.target.value)}
                            placeholder="RUN"
                            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-red-300 dark:border-red-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                          />
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={() => runCommand(cmd)}
                          disabled={isRunning || runningId !== null}
                          className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-white shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                            cmd.destructive
                              ? 'bg-red-600 hover:bg-red-700'
                              : 'bg-primary-600 hover:bg-primary-700'
                          }`}
                        >
                          {isRunning ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          Run
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Output panel */}
      <AnimatePresence>
        {lastResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className={`px-4 py-3 flex items-center justify-between ${
              lastResult.ok
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              <div className="flex items-center">
                {lastResult.ok
                  ? <CheckCircle className="w-5 h-5 mr-2" />
                  : <XCircle className="w-5 h-5 mr-2" />}
                <span className="font-semibold">{lastResult.command}</span>
                {lastResult.ok ? (
                  <span className="ml-2 text-sm">completed</span>
                ) : (
                  <span className="ml-2 text-sm">failed</span>
                )}
              </div>
              {typeof lastResult.duration_ms === 'number' && (
                <div className="flex items-center text-sm">
                  <Clock className="w-4 h-4 mr-1" />
                  {lastResult.duration_ms}ms
                </div>
              )}
            </div>
            <div className="bg-gray-900 text-gray-100 p-4 font-mono text-xs overflow-auto max-h-96">
              {lastResult.error && (
                <div className="text-red-400 mb-2">Error: {lastResult.error}</div>
              )}
              {lastResult.stdout && (
                <pre className="whitespace-pre-wrap">{lastResult.stdout}</pre>
              )}
              {lastResult.stderr && (
                <pre className="whitespace-pre-wrap text-yellow-300 mt-2">{lastResult.stderr}</pre>
              )}
              {!lastResult.stdout && !lastResult.stderr && !lastResult.error && (
                <span className="text-gray-500">(no output)</span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommandRunner;
